// TutorLayout.jsx
import React, { useEffect, useRef } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useTeachingMode } from "../contexts/TeachingModeContext";
import { useClearChat } from "../contexts/ClearChatContext";
import { useSidebar } from "../contexts/SidebarContext";
import { FaEdit, FaHistory } from "react-icons/fa";

const Tutor = () => {
  const {
    isTeachingMode,
    isWorkbookMode,
    resetToDefaultMode,
    setIsWorkbookMode,
  } = useTeachingMode();
  const { chatHistory } = useClearChat();
  const { sidebarMode, openHistoryView, closeHistoryView } = useSidebar();
  const location = useLocation();
  const wasInTutorModule = useRef(false);
  const workbookParamAppliedRef = useRef(false);

  // Reset workbook mode when navigating away from tutor module and coming back
  useEffect(() => {
    const isInTutorModule =
      location.pathname.startsWith("/tutor") ||
      location.pathname.startsWith("/practice/tutor");

    // If we were in tutor module before, navigated away, and now we're back, reset to default mode
    if (wasInTutorModule.current && !isInTutorModule) {
      // We just left the tutor module, mark that we were there
      wasInTutorModule.current = false;
    } else if (
      !wasInTutorModule.current &&
      isInTutorModule &&
      (isWorkbookMode || isTeachingMode)
    ) {
      // We just came back to tutor module and we're in a special mode, reset to default
      resetToDefaultMode();
      wasInTutorModule.current = true;
    } else if (isInTutorModule) {
      // We're in tutor module, update the ref
      wasInTutorModule.current = true;
    }
  }, [location.pathname, isWorkbookMode, isTeachingMode, resetToDefaultMode]);

  // Hide navigation when on TeachMeTopic route AND in teaching mode, OR when in workbook mode
  const shouldHideNavigation =
    (isTeachingMode && location.pathname.includes("teachMeTopic")) ||
    isWorkbookMode;

  // Automatically enable workbook mode when query flag is present
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const workbookFlag = searchParams.get("enableWorkbookmode") || "";
    const normalizedValue = workbookFlag.trim().toLowerCase();
    const shouldEnableWorkbook =
      normalizedValue !== "" &&
      normalizedValue !== "false" &&
      normalizedValue !== "0" &&
      normalizedValue !== "no";

    if (!shouldEnableWorkbook) {
      workbookParamAppliedRef.current = false;
      return;
    }

    if (workbookParamAppliedRef.current || isWorkbookMode) {
      return;
    }

    workbookParamAppliedRef.current = true;
    setIsWorkbookMode(true);
  }, [location.search, isWorkbookMode, setIsWorkbookMode]);

  useEffect(() => {
    if (shouldHideNavigation && sidebarMode === "history") {
      closeHistoryView();
    }
  }, [closeHistoryView, shouldHideNavigation, sidebarMode]);

  const handleHistoryClick = () => {
    openHistoryView();
  };

  const historyButtonTitle =
    (Array.isArray(chatHistory) ? chatHistory.length : 0) > 0
      ? "View chat history"
      : "View chat history (no chats yet)";

  return (
    <div
      className={`bg-white w-full ${
        isWorkbookMode
          ? "rounded-none sm:rounded-lg px-0 py-2 sm:p-1"
          : "rounded-lg pt-4 sm:pt-10"
      }`}
    >
      {/* Navigation links - Hide only when on TeachMeTopic and in teaching mode */}
      {!shouldHideNavigation && (
        <div className="flex flex-row justify-between w-full mb-4 sm:mb-6 overflow-x-auto pb-2 pl-10 relative">
          <div className="flex items-center gap-2">
            <NavLink
              to=""
              end
              className={({ isActive }) =>
                isActive
                  ? "bg-[#0D99FF] text-white py-2 px-4  rounded-full text-sm sm:py-3 sm:px-6 sm:rounded-4xl whitespace-nowrap "
                  : "text-center py-2 px-4 rounded-full text-sm border-[0.5px] whitespace-nowrap sm:py-3 sm:px-6 sm:rounded-4xl"
              }
            >
              Doubt Solving
            </NavLink>
            <NavLink
              to="teachMeTopic"
              className={({ isActive }) =>
                isActive
                  ? "bg-[#0D99FF] text-white py-2 px-4  rounded-full text-sm sm:py-3 sm:px-6 sm:rounded-4xl whitespace-nowrap "
                  : "text-center py-2 px-4 rounded-full text-sm border-[0.5px] whitespace-nowrap sm:py-3 sm:px-6 sm:rounded-4xl"
              }
            >
              Teach Me a Topic
            </NavLink>
          </div>

          {/* <div className="flex items-center gap-2 pr-4">
            <button
              type="button"
              onClick={handleHistoryClick}
              className="items-center p-3 pr-4 pl-4 justify-center rounded-full border border-[#0D99FF] text-[#0D99FF] transition-colors hover:bg-[#0D99FF] hover:text-white sm:flex"
              aria-label={historyButtonTitle}
              title={historyButtonTitle}
            >
              <FaHistory className="text-lg" />
              <label className="ml-2">History</label>
            </button>
          </div> */}

          {/* {hasActiveChat && (
            <button
              onClick={triggerClearChat}
              className="hidden sm:flex bg-[#0D99FF] text-white py-2 px-4 rounded-full text-sm sm:py-3 sm:px-6 sm:rounded-4xl whitespace-nowrap items-center gap-2 hover:bg-[#6B56E8] transition-colors fixed top-[calc(1rem+0.5rem)] right-20 z-50"
            >
              <FaEdit className="text-sm" />
              New Chat
            </button>
          )} */}

          {/* Evaluate My Answers button hidden
                    <NavLink
                        to="evaluate"
                        className={({ isActive }) =>
                            isActive
                                ? 'bg-[#0D99FF] text-white py-2 px-4 rounded-full text-sm sm:py-3 sm:px-6 sm:rounded-4xl whitespace-nowrap'
                                : 'text-center py-2 px-4 rounded-full text-sm border-[0.5px] whitespace-nowrap sm:py-3 sm:px-6 sm:rounded-4xl'
                        }
                    >
                        Evaluate My Answers
                    </NavLink>
                    */}

          {/*  <NavLink
                        to="teachMeTopic"
                        className={({ isActive }) =>
                            isActive
                                ? 'bg-[#0D99FF] text-white py-2 px-4 rounded-full text-sm sm:py-3 sm:px-6 sm:rounded-4xl whitespace-nowrap'
                                : 'text-center py-2 px-4 rounded-full text-sm border-[0.5px] whitespace-nowrap sm:py-3 sm:px-6 sm:rounded-4xl'
                        }
                    >
                        Teach Me a Topic
                    </NavLink> */}

          {/* <button
                        disabled
                        className="text-center py-2 px-4 rounded-full text-sm border-[0.5px] whitespace-nowrap sm:py-3 sm:px-6 sm:rounded-4xl opacity-50 cursor-not-allowed bg-gray-100"
                    >
                        Teach Me a Topic
                    </button>

                    <button
                        disabled
                        className="text-center py-2 px-4 rounded-full text-sm border-[0.5px] whitespace-nowrap sm:py-3 sm:px-6 sm:rounded-4xl opacity-50 cursor-not-allowed bg-gray-100"
                    >
                        Notes
                    </button> */}
        </div>
      )}

      {/* Main content area */}
      <div className="w-full">
        <Outlet />
      </div>
    </div>
  );
};

export default Tutor;
